import { useSyncExternalStore } from 'react';

/**
 * 「当前时间」hook。
 *
 * 为什么需要它：在渲染期间直接调用 `Date.now()` 是不纯的 —— 同一份 props
 * 会渲染出不同结果（React 的严格模式与并发渲染会立刻暴露这一点，新的
 * `react-hooks/purity` 规则也直接报错）。但「3 天后到期」「刚刚」这类相对时间
 * 又确实需要一个"现在的时刻"。
 *
 * 正确做法是把它当成**外部数据源**：用 `useSyncExternalStore` 订阅一个共享的时钟，
 * 时钟每 60 秒 tick 一次并通知所有订阅者。这样：
 * - 渲染期间读到的始终是快照（纯净、可缓存）；
 * - 所有用到相对时间的组件共用同一个定时器，不会各自开一个 interval；
 * - 页面在后台标签页时定时器被浏览器节流，不会白白耗电。
 *
 * 代价是相对时间最多滞后 60 秒 —— 对"到期/几天前"这类展示完全够用，
 * 需要秒级精度的地方（例如练习计时）应自己维护计时器。
 */

/** 快照：模块级缓存，保证同一次渲染里多次读取拿到同一个值。 */
let snapshot = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

const TICK_MS = 60_000;

function tick(): void {
  snapshot = Date.now();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) timer = setInterval(tick, TICK_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function getSnapshot(): number {
  return snapshot;
}

/** 服务端渲染/首屏兜底：没有外部时钟时也用同一个快照，避免水合不一致。 */
function getServerSnapshot(): number {
  return snapshot;
}

/** 返回一个每分钟更新一次的"当前时间"（epoch 毫秒）。 */
export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
